import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {ServerEndpoints} from "../../../server.endpoints";

/**
 * What `/Management/Telegram/GetAuthBot` answers — deliberately **not** the token.
 *
 * `telegram_auth_bot_token` is a catalogued secret (#608), so it is encrypted at rest and never leaves the
 * server. A GET that echoed it would put it back in every browser cache and every HAR file an operator sends
 * to support, which is the whole reason the catalog exists. The screen therefore reports *whether* a token is
 * stored and, when the bot handle happens to be cached, *which* bot — never the credential itself.
 */
export interface TelegramAuthBot {
  /** Whether a token is stored at all. */
  configured: boolean;
  /** The bot's `@name` when `IPlatformTelegramBot` has already resolved it, otherwise `null`. */
  botUsername: string | null;
}

/**
 * Reads and writes the platform-wide Telegram auth-bot token (PlatformOwner only).
 *
 * One bot serves the whole installation — it is the bot that carries the contact-linking conversation and the
 * legacy password bot (`docs/articles/company/contact-channel-linking.md`), not a per-company `AuthUnit` row.
 * Setting it invalidates the cached token inside the monolith, so the new bot answers without a restart; the
 * *legacy password bot* is a long-polling singleton started at boot and does not, which is why the card says
 * so in as many words.
 */
@Injectable({providedIn: 'root'})
export class TelegramAuthBotHttpService {
  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
  }

  get(): Observable<TelegramAuthBot> {
    return this.httpClient.get<TelegramAuthBot>(`${this.baseUrl}${ServerEndpoints.Management_Telegram_GetAuthBot}`);
  }

  /**
   * Stores the token. The server trims it and refuses a blank one with `E_TELEGRAM_BOT_TOKEN_BLANK`; the answer
   * is the same {@link TelegramAuthBot} the GET returns, so the card re-renders from the server's verdict
   * rather than from what was typed.
   */
  set(token: string): Observable<TelegramAuthBot> {
    return this.httpClient.put<TelegramAuthBot>(
      `${this.baseUrl}${ServerEndpoints.Management_Telegram_SetAuthBotToken}`, {token});
  }
}
